import { mutatorAction } from 'satcheljs';
import { type MailboxInfo } from 'owa-client-types';
import { getTimestamp, now } from 'owa-datetime';
import { getAsyncItemRegistry } from '../selectors/getAsyncItemRegistry';
import { type AsyncItem, type AsyncChatProviderId } from 'owa-copilot-async-chat-types';

/**
 * Writes one provider's seed results into the registry (enriching each item with provider, source
 * and account) and flips that provider's `seedStatus` to `'seeded'` in one atomic transaction.
 *
 * An item already tracked as `'thisSession'` keeps that source: the seed can resolve after a chat
 * was started locally, and `source` is what gates DAB presentation, so re-tagging it `'seeded'`
 * would drop a live card.
 */
export const seedAsyncItemRegistryMutator: (
    mailboxInfo: MailboxInfo,
    providerId: AsyncChatProviderId,
    items: ReadonlyArray<AsyncItem>
) => void = mutatorAction(
    'seedAsyncItemRegistryMutator',
    (mailboxInfo, providerId, items: ReadonlyArray<AsyncItem>) => {
        const registry = getAsyncItemRegistry(mailboxInfo);
        if (!registry) {
            return;
        }
        // One clock start for the whole seed: every item surfaces to the user at the same moment,
        // no matter how long ago the provider says its status changed.
        const seededAt = getTimestamp(now());
        for (const item of items) {
            const existing = registry.entriesById.get(item.id);
            registry.entriesById.set(item.id, {
                ...item,
                source: existing?.source === 'thisSession' ? 'thisSession' : 'seeded',
                providerId,
                mailboxInfo,
                // The seed replaces entries wholesale, so a clock already running for this status
                // has to be carried across explicitly -- a chat that changed status locally while
                // the seed was in flight has been waiting since then, and resetting it here would
                // erase that.
                statusChangedAtTimestampMs:
                    existing?.status === item.status
                        ? (existing.statusChangedAtTimestampMs ?? seededAt)
                        : seededAt,
            });
        }
        registry.seedStatusByProvider.set(providerId, 'seeded');
    }
);
